import React, { useState, useEffect } from 'react';
import {
  fetchRaidMythicBannerState,
  type RaidMythicBannerState,
} from '../services/raidMythicProgressApi';
import { RAID_PROGRESS_ENTRIES } from '../data/raidProgressData';

type WarcraftLogsProgress = RaidMythicBannerState;

const Progress: React.FC = () => {
  const [warcraftLogsData, setWarcraftLogsData] = useState<WarcraftLogsProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch mythic raid progression from guild mythic API
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      const res = await fetchRaidMythicBannerState();
      if (cancelled) return;
      if (res.ok) {
        setWarcraftLogsData(res.data);
      } else {
        setWarcraftLogsData(null);
        setError(res.error);
      }
      if (!cancelled) setLoading(false);
    };

    run().catch((err) => {
      if (!cancelled) {
        console.error('Error fetching raid mythic progress:', err);
        setError('Unable to load live progression data');
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);
  const raids = RAID_PROGRESS_ENTRIES;

  return (
    <div className="page-container">
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <h1 className="text-4xl md:text-5xl font-bold mb-8 text-center">
          <span className="gradient-text">Raid Progress</span>
        </h1>
        
        <div className="max-w-4xl mx-auto">
          {/* Live mythic raid progress (guild API) */}
          {loading && (
            <div className="glass p-6 rounded-xl mb-6">
              <div className="flex justify-center items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
                <span className="ml-3 text-text-secondary">Loading live progression data...</span>
              </div>
            </div>
          )}
          
          {error && (
            <div className="glass p-6 rounded-xl mb-6">
              <div className="text-center">
                <div className="text-red-400 mb-2">⚠️ {error}</div>
                <div className="text-sm text-text-muted">Falling back to static data</div>
              </div>
            </div>
          )}
          
          {warcraftLogsData && !error && (
            <div className="glass p-6 rounded-xl mb-6 relative overflow-hidden">
           {/* Background boss image */}
           <div 
             className="absolute inset-0 bg-cover bg-center bg-no-repeat rounded-xl opacity-20"
             style={{
               backgroundImage: warcraftLogsData.bossImageUrl
                 ? `url(${warcraftLogsData.bossImageUrl})`
                 : undefined,
               filter: 'blur(1px)'
             }}
           ></div>
              
              {/* Fallback gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-cyan-900/10 to-purple-900/20 rounded-xl"></div>
              
              <div className="relative z-10">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center gap-4">
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        {warcraftLogsData.guildName}
                      </h3>
                      <div className="w-full h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 mt-1"></div>
                    </div>
                  </div>
                </div>

                {/* Progress Section */}
                <div className="flex justify-between items-end">
                  <div>
                    <div className="text-sm font-bold text-white mb-1">PROGRESS</div>
                    <div className="text-3xl font-bold text-white">
                      {warcraftLogsData.progress}/{warcraftLogsData.totalBosses}M
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-sm font-bold text-green-400 mb-2">
                      {warcraftLogsData.isComplete ? 'COMPLETE!' : 'IN PROGRESS'}
                    </div>
                    <div className="text-lg font-bold text-white">
                      {warcraftLogsData.currentBoss?.name?.toUpperCase() || 'FRACTILLUS'}
                    </div>
                  </div>
                </div>

                {/* Bottom info */}
                <div className="mt-4 pt-4 border-t border-gray-600/30 flex justify-between items-center text-xs text-gray-400">
                  <div>Last updated: {warcraftLogsData.lastUpdated}</div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span>Live data (guild API)</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="grid gap-6">
            {raids.map((raid, index) => (
              <div key={`${raid.name}-${raid.difficulty}-${index}`} className="glass p-6 rounded-xl">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{raid.name}</h3>
                    <p className="text-text-secondary">{raid.difficulty}</p>
                  </div>
                  <div className="mt-4 md:mt-0 text-right">
                    <div className="text-2xl font-bold neon-cyan-text mb-1">{raid.progress}</div>
                    <div className="text-sm text-text-secondary">{raid.status}</div>
                    <div className="text-xs text-text-muted">{raid.date}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Progress; 